import { useCallback } from "react";
import { AxiosError } from "axios";
import { api } from "config";
import { PATHS } from "constants/index";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useLoading } from "state/loading/hooks";
import { useUser } from "state/user/hooks";
import { UserType } from "state/user/types";
import { ResetPassword, UserCredentials, UserEmail } from "types";

const FALLBACK_ERROR = "Some error has occurred! Please try again.";

/**
 * Pull a displayable message out of an API error.
 *
 * The backend returns validation failures as
 *   { success: false, error: [ { <field>: ["message", ...] } ] }
 * where <field> varies: "non_field_errors" for bad credentials, but "email" or
 * "password" for field-level validation. Reaching for one specific key threw
 * ("Cannot read properties of undefined") whenever a different key came back,
 * which hid the real message. This reads the first message regardless of key.
 */
const getApiErrorMessage = (e: unknown): string => {
  if (!(e instanceof AxiosError)) return FALLBACK_ERROR;

  const data = e.response?.data;
  const errors = data?.error;

  if (Array.isArray(errors)) {
    for (const entry of errors) {
      if (typeof entry === "string" && entry) return entry;
      if (entry && typeof entry === "object") {
        for (const value of Object.values(entry as Record<string, unknown>)) {
          if (typeof value === "string" && value) return value;
          if (Array.isArray(value) && typeof value[0] === "string") return value[0];
        }
      }
    }
  }

  if (typeof errors === "string" && errors) return errors;
  if (typeof data?.detail === "string" && data.detail) return data.detail;

  return FALLBACK_ERROR;
};

export const useAuth = () => {
  const { isLoading, setIsLoading } = useLoading();
  const { setUser } = useUser();
  const navigate = useNavigate();

  // const getUserCredits = useCallback(async (key: string) => {
  //   const { data } = await api.get("user/details/", {
  //     headers: {
  //       Authorization: "Token " + key,
  //     },
  //   });
  //   return data.data.credits;
  // }, []);

  const loginUser = useCallback(
    async (credentials: UserCredentials) => {
      try {
        setIsLoading(true);
        const { data } = await api.post("login/", credentials);
        const user: UserType = {
          key: data.key,
          id: data.id,
          name: data.first_name + data.last_name,
          isClient: data.is_client,
          lname: data.last_name,
          fname: data.first_name,
          credits: data.credits,
          kit_credits:data.kit_credits,
          is_SuperAdmin:data.is_SuperAdmin,
        };
        toast.success("Login Successful.");
        setUser({ user, isAuthenticated: true });
      } catch (e) {
        toast.error(getApiErrorMessage(e));
      } finally {
        setIsLoading(false);
      }
    },
    [setIsLoading, setUser]
  );
  const logoutUser = useCallback(() => {
    toast.success("Logout Successful.");
    setUser({ user: undefined, isAuthenticated: false });
  }, [setUser]);

  const resetPassword = useCallback(
    async (email: UserEmail) => {
      try {
        setIsLoading(true);
        await api.post("/password/reset/", email);
        toast.success("Email Sent Successfully.");
        navigate(PATHS.LOGIN);
      } catch (e) {
        toast.error(getApiErrorMessage(e));
      } finally {
        setIsLoading(false);
      }
    },
    [setIsLoading, navigate]
  );
  const changePassword = useCallback(
    async (credentials: ResetPassword) => {
      try {
        setIsLoading(true);
        await api.post(
          `/password/reset/confirm/`,
          credentials
        );
        toast.success("Password Changed Successfully.");
        navigate(PATHS.LOGIN);
      } catch (e) {
        toast.error("Some error has occurred! Please try again.");
      } finally {
        setIsLoading(false);
      }
    },
    [setIsLoading, navigate]
  );

  return {
    loginUser,
    logoutUser,
    isLoading,
    resetPassword,
    changePassword,
  };
};
